import Axios from "axios";
import Cookies from 'js-cookie'
import { sha256 } from 'js-sha256'
import { v4 as uuidv4 } from 'uuid'
import { error, log } from "./Log";
import { FACEBOOK_PIXEL_ID, OAUTH_API } from "./Configuration";

// Pixel is the front-end APi that sends data to Meta, Docs:
// https://developers.facebook.com/docs/meta-pixel/reference

const hashing = (data?: string) => {
  return typeof data === 'string' ? sha256(data.trim().toLowerCase()) : null
}
const parseUserData = (email?: string, phone?: string) => ({
  "fbc": Cookies.get('_fbc'),
  "fbp": Cookies.get('_fbp'),
  "em": hashing(email),
  "ph": hashing(phone),
  "client_user_agent": navigator?.userAgent || '',
})

type FacebookPixelContentIds = Array<number | string>
type FacebookPixelContentType = 'product' | 'product_group'
type FacebookPixelContents = Array<{ id: string, quantity: number | null, item_price: number | null }>

type CoversionAPIPayload<T = undefined> = {
  user?: UserData | null,
  eventID?: string
} & T
type PixelSafeguardHandler<T = any> = (eventName: string, eventPayload?: CoversionAPIPayload<T>, id?: { eventID: string }) => Promise<{ success: boolean }> | { success: boolean }
const Pixel: PixelSafeguardHandler = async (eventName, eventPayload = undefined, { eventID } = { eventID: uuidv4() }) => {
  const user: UserData | undefined = eventPayload?.user
  try {
    if (user) {
      delete eventPayload['user']
    }
    if (typeof window !== 'undefined' && typeof window['fbq'] === 'function') {
      await window['fbq']('track', eventName, eventPayload, { eventID });
    } else {
      error('Either window is not an object or fbq is not a function')
    }
    if (typeof eventPayload === 'object') {
      const event_time = Math.floor((Date.now() / 1000))
      const source_url = window.location.href
      const event_id = eventID
      const userdata = parseUserData(user?.email, user?.phone)

      Axios.post(`${OAUTH_API}/sf/marketing/facebookEvent/${eventName}`, {
        ...eventPayload,
        ...userdata,
        event_time,
        source_url,
        event_id,
      })
    }
    return { success: true }
  } catch (err) {
    error(`Pixel ${eventName} Failed ${err}`)
  }
  return { success: false }
}
type UserData = { email: string, phone: string }
type FacebookAddToCartPayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  content_name: string,
  content_type?: FacebookPixelContentType,
  content_category: string,
  contents: FacebookPixelContents,
  currency?: string,
  value: number
}>
type AddToCartHandler = (item: FacebookAddToCartPayload) => Promise<{ success: boolean }> | { success: boolean }
const addToCart: AddToCartHandler = ({
  content_ids,
  content_category,
  content_name,
  content_type = 'product',
  currency = 'USD',
  contents,
  value,
  user,
}) => Pixel('AddToCart', {
  content_ids,
  content_category,
  content_name,
  content_type,
  contents,
  currency,
  value,
  user,
})

type FacebookViewContentPayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  content_name: string,
  content_type?: FacebookPixelContentType,
  content_category: string,
  contents: FacebookPixelContents,
  currency?: string,
  value: number
}>

type ViewContentHandler = (item: FacebookViewContentPayload) => Promise<{ success: boolean }> | { success: boolean }
const viewContent: ViewContentHandler = ({
  content_ids,
  content_category,
  content_name,
  content_type = 'product',
  currency = 'USD',
  contents,
  value,
  user,
}) => Pixel('ViewContent', {
  content_ids,
  content_category,
  content_name,
  content_type,
  currency,
  contents,
  value,
  user,
})

type FacebookInitializeCheckoutPayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  content_name: string,
  content_type?: FacebookPixelContentType,
  content_category: string,
  contents: FacebookPixelContents,
  currency?: string,
  value: number,
  num_items: number,
}>

type InitializeCheckoutHandler = (item: FacebookInitializeCheckoutPayload) => Promise<{ success: boolean }> | { success: boolean }
const initializeCheckout: InitializeCheckoutHandler = ({
  content_ids,
  contents,
  content_type = 'product',
  currency = 'USD',
  value,
  num_items,
  user,
}) => Pixel('InitiateCheckout', {
  content_ids,
  contents,
  content_type,
  currency,
  num_items,
  value,
  user,
})


type TrackPurchasePayload = CoversionAPIPayload<{
  content_ids: FacebookPixelContentIds,
  order_id: string,
  content_name: string,
  content_type?: FacebookPixelContentType,
  content_category: string,
  contents: FacebookPixelContents,
  currency?: string,
  value: number,
  num_items: number,
}>


type TrackPurchaseHandler = (item: TrackPurchasePayload) => Promise<{ success: boolean }> | { success: boolean }
const trackPurchase: TrackPurchaseHandler = ({
  content_ids,
  order_id,
  content_type = 'product',
  contents,
  value,
  currency = 'USD',
  num_items,
  user,
}) => Pixel('Purchase', {
  content_ids,
  order_id,
  content_type,
  contents,
  value,
  currency,
  num_items,
  user,
}, { eventID: order_id })


type CompleteRegistrationPayload = CoversionAPIPayload<{
  content_name: string,
  currency?: string,
  status?: boolean,
  value: number,
}>

type CompleteRegistrationHandler = (item: CompleteRegistrationPayload) => Promise<{ success: boolean }> | { success: boolean }
const completeRegistration: CompleteRegistrationHandler = ({
  content_name,
  status = true,
  currency = 'USD',
  value,
  user,
}) => Pixel('CompleteRegistration', {
  content_name,
  status,
  currency,
  value,
  user,
})


type SearchPayload = CoversionAPIPayload<{
  search_string: string,
  content_ids: FacebookPixelContentIds,
  contents: FacebookPixelContents,
  currency?: string,
  value?: number,
}>

type SearchHandler = (item: SearchPayload) => Promise<{ success: boolean }> | { success: boolean }
const search: SearchHandler = ({
  search_string,
  content_ids,
  contents,
  currency = 'USD',
  value = 0,
  user,
}) => Pixel('Search', {
  content_category: 'Product Search',
  content_ids,
  contents,
  currency,
  search_string,
  value,
  user,
})

type PixelHandler = () => Promise<{ success: boolean }> | { success: boolean }
const customizeProduct: PixelHandler = () => Pixel('CustomizeProduct')

const pageView: PixelHandler = () => Pixel('PageView')

const init = async () => {
  try {
    log(`typeof fbq = ${typeof window['fbq']}`, { FACEBOOK_PIXEL_ID })
    window['fbq']?.('init', FACEBOOK_PIXEL_ID)
    pageView()
  } catch (err) {
    error(`Failed to Initialize Facebook Pixel ${err}`)
  }
}

const Facebook = {
  customizeProduct,
  search,
  completeRegistration,
  trackPurchase,
  initializeCheckout,
  viewContent,
  addToCart,
  pageView,
  init,
  hashing,
  parseUserData,
}


export default Facebook
